import React, { useEffect, useState } from "react";
import "./DriverDashboard.css";
import MeetingDatePicker from "./MeetingDatePicker";
import {
  watchAssignedRequests,
  watchOpenRequests,
  cancelDriverHelp,
} from "../services/firebaseApi";
import { listenToHabitualCarpools } from "../services/habitualCarpoolsService";
import RequestNearbyDriversModal from "./RequestNearbyDriversModal";
import ConfirmDialog from "./ConfirmDialog";
import { formatMeetingDateTime, toIsoDate } from "../utils/dateHelpers";
import { getEventColorForDate, getColorWithOpacity } from "../utils/eventColorHelper";

const DriverDashboard = ({ onBack, onGoToDriverForm }) => {
  const [showAccepted, setShowAccepted] = useState(false);
  const [filterMeetingAt, setFilterMeetingAt] = useState(null); // null = Toutes
  const [openRequests, setOpenRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [modalRequest, setModalRequest] = useState(null);
  const [habitualCarpools, setHabitualCarpools] = useState([]);

  // État pour la confirmation d'annulation
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [eventColors, setEventColors] = useState(new Map()); // Map: requestId -> color

  // Charger les covoiturages habituels (configurés via admin)
  useEffect(() => {
    const unsubscribe = listenToHabitualCarpools((items) => {
      setHabitualCarpools(items || []);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Charger les demandes ouvertes selon le filtre
  useEffect(() => {
    const unsubscribe = watchOpenRequests(
      (rows) => setOpenRequests(rows),
      (err) => console.error("watch error:", err)
    );
    return () => { try { unsubscribe && unsubscribe(); } catch {} };
  }, [filterMeetingAt]);

  // Charger toutes les demandes acceptées (filtrables par date)
  useEffect(() => {
    const unsubscribe = watchAssignedRequests(
      (rows) => setAcceptedRequests(rows),
      (err) => console.error("watch error:", err)
    );
    return () => { try { unsubscribe && unsubscribe(); } catch {} };
  }, [filterMeetingAt]);

  // Charger les couleurs des événements pour les demandes en attente
  useEffect(() => {
    const loadEventColors = async () => {
      const colorsMap = new Map();
      for (const r of openRequests) {
        if (r.meetingAt) {
          const isoDate = r.isoDate || toIsoDate(r.meetingAt);
          const color = await getEventColorForDate(isoDate);
          if (color) {
            colorsMap.set(r.id, color);
          }
        }
      }
      setEventColors(colorsMap);
    };
    if (openRequests.length > 0) {
      loadEventColors();
    }
  }, [openRequests]);

  // Charger les couleurs des événements pour les demandes acceptées
  useEffect(() => {
    const loadEventColors = async () => {
      const colorsMap = new Map();
      for (const r of acceptedRequests) {
        if (r.meetingAt) {
          const isoDate = r.isoDate || toIsoDate(r.meetingAt);
          const color = await getEventColorForDate(isoDate);
          if (color) {
            colorsMap.set(r.id, color);
          }
        }
      }
      setEventColors(colorsMap);
    };
    if (acceptedRequests.length > 0) {
      loadEventColors();
    }
  }, [acceptedRequests]);

  // Fonction pour annuler l'aide
  const handleCancelHelp = (request) => {
    setRequestToCancel(request);
    setConfirmCancelOpen(true);
  };

  // Fonction pour confirmer l'annulation
  const handleConfirmCancel = async () => {
    if (!requestToCancel) return;

    try {
      const result = await cancelDriverHelp({
        requestId: requestToCancel.id,
        driverName: requestToCancel.driverName,
      });

      if (result.ok) {
        // La demande sera automatiquement mise à jour via Firebase
        setConfirmCancelOpen(false);
        setRequestToCancel(null);
      } else {
        alert("Erreur lors de l'annulation. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
      alert("Une erreur est survenue lors de l'annulation.");
    }
  };

  return (
    <div className="driver-dashboard">
      <div className="dashboard-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <h2>🚗 Tableau de bord des conducteurs</h2>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-button ${!showAccepted ? "active" : ""}`}
          onClick={() => setShowAccepted(false)}
        >
          Demandes en attente ({openRequests.length})
        </button>
        <button
          className={`tab-button ${showAccepted ? "active" : ""}`}
          onClick={() => setShowAccepted(true)}
        >
          Demandes acceptées ({acceptedRequests.length})
        </button>
      </div>

      <div className="filter-section" style={{ margin: "12px 0" }}>
        <p>
          <strong>Filtrer par date :</strong>
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            className={`tab-button ${filterMeetingAt === null ? "active" : ""}`}
            onClick={() => setFilterMeetingAt(null)}
          >
            Toutes
          </button>
          <MeetingDatePicker
            value={filterMeetingAt}
            onChange={(m) => setFilterMeetingAt(m.meetingAt)}
            limit={8}
          />
        </div>
      </div>

      <div className="requests-container">
        {!showAccepted ? (
          // Demandes en attente
          <div className="requests-list">
            {openRequests.length === 0 ? (
              <div className="no-requests">
                <p>📭 Aucune demande correspondant au filtre</p>
                <p>Les demandes apparaîtront ici en temps réel.</p>
              </div>
            ) : (
              openRequests.map((request) => (
                <div
                  key={request.id}
                  className="request-card"
                  onClick={() => setModalRequest(request)}
                  style={{ 
                    cursor: "pointer",
                    background: eventColors.get(request.id) ? getColorWithOpacity(eventColors.get(request.id), 0.15) : undefined
                  }}
                >
                  <div className="request-header">
                    <h3>🚶 {request.name || request.passengerName}</h3>
                  </div>

                  <div className="request-details">
                    <p>
                      <strong>Adresse :</strong> {request.address}
                    </p>
                    <p>
                      <strong>Ville :</strong> {request.city} (
                      {request.postalCode})
                    </p>
                  </div>

                  <div className="request-actions">
                    <button
                      className="accept-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalRequest(request);
                      }}
                    >
                      Je le prends
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          // Demandes acceptées
          <div className="requests-list">
            {acceptedRequests.length === 0 ? (
              <div className="no-requests">
                <p>📋 Aucune demande acceptée pour le moment</p>
              </div>
            ) : (
              acceptedRequests.map((request) => (
                <div 
                  key={request.id} 
                  className="request-card accepted"
                  style={{
                    background: eventColors.get(request.id) ? getColorWithOpacity(eventColors.get(request.id), 0.15) : undefined
                  }}
                >
                  <div className="request-header">
                    <h3>🚶 {request.name || request.passengerName}</h3>
                  </div>

                  <div className="request-details">
                    <p>
                      <strong>Adresse :</strong> {request.address}
                    </p>
                    <p>
                      <strong>Ville :</strong> {request.city} (
                      {request.postalCode})
                    </p>
                    <p>
                      <strong>Conducteur :</strong> {request.driverName}
                    </p>
                  </div>

                  {/* Bouton pour annuler l'aide */}
                  <div className="request-actions">
                    <button
                      type="button"
                      className="cancel-help-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelHelp(request);
                      }}
                      style={{
                        background: "#dc2626",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "background-color 0.2s",
                      }}
                    >
                      🚫 Annuler mon aide
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {modalRequest && (
        <RequestNearbyDriversModal
          request={modalRequest}
          onClose={() => setModalRequest(null)}
          onGoToDriverForm={onGoToDriverForm}
        />
      )}

      <div className="dashboard-info">
        <p>
          💡 <strong>Conseils :</strong>
        </p>
        <ul>
          <li>
            Cliquez sur une demande pour choisir le conducteur et confirmer
          </li>
          <li>Les demandes sont mises à jour en temps réel</li>
          <li>
            Ne s'affichent pas les demandes passées (au-delà de 2h après la
            réunion)
          </li>
        </ul>
      </div>

      <div className="habitual-carpools">
        <p>
          🔁 <strong>Covoiturages habituels</strong>
        </p>
        {habitualCarpools.length === 0 ? (
          <div className="habitual-empty">
            <p>Aucun covoiturage habituel configuré.</p>
          </div>
        ) : (
          <div className="habitual-list">
            {habitualCarpools
              .slice()
              .sort((a, b) => {
                const ad = String(a.driverName || "").toLowerCase();
                const bd = String(b.driverName || "").toLowerCase();
                const ap = String(a.passengerName || "").toLowerCase();
                const bp = String(b.passengerName || "").toLowerCase();
                return ad.localeCompare(bd) || ap.localeCompare(bp);
              })
              .map((item) => (
                <div key={item.id} className="habitual-row">
                  <span className="habitual-driver">{item.driverIcon || "🚗"} {item.driverName}</span>
                  <span className="habitual-arrow">→</span>
                  <span className="habitual-passenger">{item.passengerIcon || "👤"} {item.passengerName}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modale de confirmation pour annuler l'aide */}
      <ConfirmDialog
        open={confirmCancelOpen}
        onClose={() => {
          setConfirmCancelOpen(false);
          setRequestToCancel(null);
        }}
        title="Confirmer l'annulation"
        message={
          requestToCancel ? (
            <div>
              Êtes-vous sûr de vouloir annuler votre aide pour cette demande ?
              <div className="text-xs text-gray-500 mt-2">
                {requestToCancel.name || requestToCancel.passengerName} —{" "}
                {requestToCancel.meetingAt
                  ? formatMeetingDateTime(requestToCancel.meetingAt)
                  : "Date non précisée"}
              </div>
            </div>
          ) : null
        }
        confirmLabel="Oui, annuler mon aide"
        cancelLabel="Non, garder mon aide"
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default DriverDashboard;
